/* eslint-disable no-use-before-define */

import React, { useState, useCallback, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import ModernView, { MapControls } from '../components/map/ModernView';
import NodeDetailsPanel from '../components/panels/NodeDetailsPanel';
import MessagesPanel from '../components/panels/MessagesPanel';
import { Analytics } from '@vercel/analytics/react';
import { useQuery } from '@tanstack/react-query';
import { fetchNetworkSummary, fetchMessages, fetchPackets, fetchPacketStatistics, fetchCompleteNodeData } from '../services/api';
import { Message, Packet } from '../types/api';
import LatestPacketInfo from '../components/header/LatestPacketInfo';
import { FiRefreshCw, FiMenu, FiChevronLeft, FiChevronRight, FiChevronDown, FiChevronUp, FiInfo } from 'react-icons/fi';
import Sidebar from '../components/layout/Sidebar';
import InfoModal from '../components/ui/InfoModal';

// Define a constant refresh interval
const REFRESH_INTERVAL_MS = 30000; // 30 seconds
const PACKETS_REFRESH_INTERVAL_MS = 2500; // 2.5 seconds for packets

/**
 * Main Dashboard page component.
 * Contains the map view and dashboard panels.
 */
const Dashboard: React.FC = () => {
  // State for selected node
  const [selectedNodeId, setSelectedNodeId] = useState<number | undefined>(undefined);
  
  // Add state for forcing a re-render of the NodeDetailsPanel
  const [forceRender, setForceRender] = useState<number>(0);
  
  // Add state for mobile detection
  const [isMobile, setIsMobile] = useState<boolean>(false);
  
  // Reference to the node details panel
  const nodeDetailsPanelRef = useRef<HTMLDivElement>(null);
  
  // Reference to the map view (now always ModernView)
  const mapViewRef = useRef<any>(null);
  
  // State for last updated timestamp
  const [lastUpdated, setLastUpdated] = useState<string>(
    new Date().toLocaleTimeString()
  );
  
  // State for sidebar collapse
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  
  // State for showing offline nodes
  const [showOfflineNodes, setShowOfflineNodes] = useState<boolean>(false);
  
  // State for showing debug info
  const [showDebug, setShowDebug] = useState<boolean>(false);
  
  // State for map legend collapse
  const [legendCollapsed, setLegendCollapsed] = useState<boolean>(true);
  
  // State for latest packet info collapse
  const [packetInfoCollapsed, setPacketInfoCollapsed] = useState<boolean>(false);
  
  // State for info modal
  const [infoModalOpen, setInfoModalOpen] = useState<boolean>(false);
  
  // Toggle sidebar collapse
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);
  
  // Check if device is mobile on component mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Trigger window resize event when sidebar state changes
  // This forces the map to recalculate its dimensions
  useEffect(() => {
    // Small delay to allow the CSS transition to complete
    const resizeTimer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 300);
    
    return () => clearTimeout(resizeTimer);
  }, [sidebarCollapsed]);
  
  // Fetch complete node data
  const { 
    data: nodePositions,
    isLoading: isLoadingPositions,
    error: positionsError
  } = useQuery({
    queryKey: ['completeNodeData'],
    queryFn: fetchCompleteNodeData,
    refetchInterval: REFRESH_INTERVAL_MS, // Use constant interval
  });
  
  // Fetch network summary
  const {
    data: networkSummary,
    isLoading: isLoadingSummary,
    error: summaryError
  } = useQuery({
    queryKey: ['networkSummary'],
    queryFn: fetchNetworkSummary,
    refetchInterval: REFRESH_INTERVAL_MS, // Use constant interval
  });
  
  // Fetch packets for the header display
  const {
    data: packets,
    isLoading: isLoadingPackets,
    error: packetsError
  } = useQuery({
    queryKey: ['packets'],
    queryFn: () => fetchPackets(5), // Fetch the 5 most recent packets
    refetchInterval: PACKETS_REFRESH_INTERVAL_MS, // Use faster refresh for packets
  });
  
  // Fetch packet statistics
  const {
    data: packetStatistics,
    isLoading: isLoadingPacketStatistics,
    error: packetStatisticsError
  } = useQuery({
    queryKey: ['packetStatistics'],
    queryFn: fetchPacketStatistics,
    refetchInterval: REFRESH_INTERVAL_MS, // Use constant interval
  });
  
  // Get the last packet for header display
  const lastPacket = packets && packets.length > 0 ? packets[0] : null;
  
  // Debug logging
  useEffect(() => {
    console.log('Packets data:', packets);
    console.log('Last packet:', lastPacket);
    console.log('Packets loading:', isLoadingPackets);
    console.log('Packets error:', packetsError);
  }, [packets, lastPacket, isLoadingPackets, packetsError]);
  
  // Access the query client for manual refetching
  const queryClient = useQueryClient();

  // Handle node selection
  const handleNodeSelect = useCallback((nodeId: number | undefined) => {
    if (nodeId === undefined) {
      // Handle explicit deselection from close button
      console.log('Explicit deselection (undefined), clearing selected node');
      setSelectedNodeId(undefined);
      return;
    }
    
    console.log(`Node selected: ${nodeId}, previously selected: ${selectedNodeId}`);
    
    // Force a re-render when switching between nodes
    if (selectedNodeId !== nodeId) {
      setForceRender(prev => prev + 1);
    }
    
    // Directly set the new node ID
    setSelectedNodeId(nodeId);
    
    // Make sure the sidebar is expanded when a node is selected
    if (sidebarCollapsed) {
      setSidebarCollapsed(false);
    }
    
    // Only scroll the panel into view on desktop
    if (!isMobile) {
      // Scroll the panel into view after a short delay to ensure the panel is rendered
      setTimeout(() => {
        if (nodeDetailsPanelRef.current) {
          nodeDetailsPanelRef.current.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    } else {
      // On mobile, toggle the sidebar to show it instead of scrolling
      setSidebarCollapsed(false);
    }
  }, [selectedNodeId, isMobile, sidebarCollapsed]);

  // Handle refresh data
  const handleRefresh = useCallback(() => {
    console.log('Refreshing data...');
    
    // Update last updated timestamp
    setLastUpdated(new Date().toLocaleTimeString());
    
    // Invalidate and refetch all queries
    queryClient.invalidateQueries({ queryKey: ['networkSummary'] });
    queryClient.invalidateQueries({ queryKey: ['completeNodeData'] });
    queryClient.invalidateQueries({ queryKey: ['nodeConnections'] });
    queryClient.invalidateQueries({ queryKey: ['packets'] });
    queryClient.invalidateQueries({ queryKey: ['packetStatistics'] });
    
    // If a node is selected, also refetch its details
    if (selectedNodeId) {
      queryClient.invalidateQueries({ queryKey: ['nodeDetails', selectedNodeId] });
      queryClient.invalidateQueries({ queryKey: ['messages', selectedNodeId] });
      queryClient.invalidateQueries({ queryKey: ['nodeTelemetry', selectedNodeId] });
    } else {
      // If no node is selected, refetch general messages
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    }
  }, [queryClient, selectedNodeId]);

  // Set up automatic refresh based on user preferences
  useEffect(() => {
    const intervalId = setInterval(() => {
      handleRefresh();
    }, REFRESH_INTERVAL_MS); // Use constant interval
    
    return () => clearInterval(intervalId);
  }, [handleRefresh]);

  // Auto-center/zoom the map on load
  useEffect(() => {
    const initializeMap = () => {
      if (mapViewRef.current?.autoCenter) {
        console.log('Initializing map view from Dashboard');
        mapViewRef.current.autoCenter(true); // Use force=true
      }
    };
    
    // Small delay to ensure the map is properly loaded
    const timer = setTimeout(initializeMap, 500);
    return () => clearTimeout(timer);
  }, []);

  // Get summary data for header
  const summary = networkSummary || {
    totalNodes: nodePositions?.length || 0,
    onlineNodes: nodePositions?.filter(node => node.isOnline).length || 0,
    offlineNodes: (nodePositions?.length || 0) - (nodePositions?.filter(node => node.isOnline).length || 0),
    messagesLast24h: 0,
    lastUpdated: new Date().toISOString()
  };

  // Handle new packet: forward the packet to MapView so that it can highlight the node purple.
  const handlePacket = useCallback((packet: Packet) => {
    if (mapViewRef.current && typeof mapViewRef.current.handleNewPacket === 'function') {
      // Simply pass the packet to MapView which will handle scrolling internally
      mapViewRef.current.handleNewPacket(packet);
    } else {
      console.warn('MapView ref not available for new packet highlighting', packet);
    }
  }, [mapViewRef]);

  return (
    <DashboardContainer>
      <Header>
        <LogoSection>
          <Logo onClick={() => setInfoModalOpen(true)}>NHMesh Live</Logo>
          <InfoButton onClick={() => setInfoModalOpen(true)}>
            <FiInfo size={18} />
            <InfoButtonText>About</InfoButtonText>
          </InfoButton>
        </LogoSection>
        
        <NetworkSummarySection>
          <SummaryItem>
            <SummaryLabel>
              <DesktopOnlyText>Nodes</DesktopOnlyText>
              <MobileOnlyText>Nds</MobileOnlyText>
            </SummaryLabel>
            <SummaryValue>
              <OnlineCount>{summary.onlineNodes}</OnlineCount>
              <TotalCount>/{summary.totalNodes}</TotalCount>
            </SummaryValue>
          </SummaryItem>
          
          <SummaryItem>
            <SummaryLabel>
              <DesktopOnlyText>Packets (1h/24h)</DesktopOnlyText>
              <MobileOnlyText>Pkts (1h/24h)</MobileOnlyText>
            </SummaryLabel>
            <SummaryValue>
              <PacketCount>{packetStatistics?.lastHour || 0}</PacketCount> / <PacketCount>{packetStatistics?.last24Hours || 0}</PacketCount>
            </SummaryValue>
          </SummaryItem>
          

        </NetworkSummarySection>
        
        <HeaderControls>
          <LastUpdatedText>Updated: {lastUpdated}&nbsp;&nbsp;</LastUpdatedText>
          <RefreshButton onClick={handleRefresh}>
            <FiRefreshCw style={{ marginRight: '5px' }} />
          </RefreshButton>
          <MenuButton onClick={toggleSidebar}>
            <FiMenu />
          </MenuButton>
        </HeaderControls>
      </Header>
      
      <MainContent $sidebarCollapsed={sidebarCollapsed}>
        {/* Add backdrop for mobile sidebar */}
        {!sidebarCollapsed && isMobile && (
          <SidebarBackdrop onClick={toggleSidebar} />
        )}
        
        <Sidebar collapsed={sidebarCollapsed}>
          <SidebarContent $legendCollapsed={legendCollapsed}>
            {/* Node Details Panel - show only when a node is selected */}
            {selectedNodeId !== undefined && (
              <div 
                ref={nodeDetailsPanelRef}
                style={{
                  flex: '0 0 auto',
                  minHeight: '380px', 
                  height: 'auto',
                  maxHeight: isMobile ? '50%' : '60%',
                  overflowY: 'auto',
                  backgroundColor: isMobile ? 'rgba(45, 55, 72, 0.1)' : 'rgba(45, 55, 72, 0.05)',
                  borderRadius: '8px',
                  padding: '4px',
                  marginBottom: '4px',
                  boxShadow: isMobile ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
                  border: isMobile ? '1px solid rgba(52, 152, 219, 0.2)' : 'none'
                }}
              >
                <NodeDetailsPanel 
                  key={`node-${selectedNodeId}-${forceRender}`} /* Key ensures React creates a new component instance */
                  nodeId={selectedNodeId}
                  onClose={() => setSelectedNodeId(undefined)}
                />
              </div>
            )}
            
            {/* Messages Panel - Always shown */}
            <div style={{ flex: '1 1 auto', overflow: 'hidden', minHeight: 0 }}>
              <MessagesPanel 
                nodeId={selectedNodeId} 
                limit={selectedNodeId ? 20 : 30} 
              />
            </div>
            
            {/* Map Legend Section - Moved to bottom */}
            <div style={{ flex: '0 0 auto', marginTop: '4px' }}>
              <MapControls 
                showDebug={showDebug}
                setShowDebug={setShowDebug}
                connectionTypes={mapViewRef.current?.activeConnectionTypes || []}
                collapsed={legendCollapsed}
                onToggleCollapse={() => setLegendCollapsed(!legendCollapsed)}
              />
            </div>
          </SidebarContent>
        </Sidebar>
        
        <SidebarToggle onClick={toggleSidebar} $collapsed={sidebarCollapsed}>
          {sidebarCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
        </SidebarToggle>
        
        <MapContainer>
          <ModernView 
            ref={mapViewRef}
            onNodeSelect={handleNodeSelect}
            selectedNodeId={selectedNodeId}
            showOfflineNodes={showOfflineNodes}
            autoCenter={false}
            showDebug={showDebug}
            onShowDebugChange={setShowDebug}
          />
          <LatestPacketInfoWrapper>
            <PacketInfoContainer>
              {!packetInfoCollapsed ? (
                <>
                  <LatestPacketInfo 
                    onNewPacket={handlePacket} 
                    showOfflineNodes={showOfflineNodes} 
                    onToggleShowOfflineNodes={() => setShowOfflineNodes(prev => !prev)}
                    capitalizeType={true}
                    nodePositions={nodePositions}
                    packets={packets || []}
                  />
                  <CollapseToggle onClick={() => setPacketInfoCollapsed(true)}>
                    <FiChevronUp />
                  </CollapseToggle>
                </>
              ) : (
                <CollapsedPacketButton onClick={() => setPacketInfoCollapsed(false)}>
                  Latest Packets <FiChevronDown />
                </CollapsedPacketButton>
              )}
            </PacketInfoContainer>
          </LatestPacketInfoWrapper>
        </MapContainer>
      </MainContent>
      
      {/* Add Vercel Analytics component for SPA tracking */}
      <Analytics mode="auto" />
      
      {/* Add InfoModal */}
      <InfoModal isOpen={infoModalOpen} onClose={() => setInfoModalOpen(false)} />
    </DashboardContainer>
  );
};

/**
 * Styled container for the entire dashboard.
 */
const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  position: relative;
`;

/**
 * Styled header container
 */
const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: #2c3e50;
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 640px) {
    padding: 10px 15px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 10px;
  }
`;

/**
 * Styled logo section
 */
const LogoSection = styled.div`
  flex: 0 0 auto;
  margin-right: 30px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  
  @media (max-width: 640px) {
    margin-right: 15px;
    gap: 2px;
  }
  
  @media (max-width: 480px) {
    margin-right: 8px;
  }
`;

/**
 * Styled logo
 */
const Logo = styled.h1`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: white;
  cursor: pointer;
  transition: color ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primaryLight};
  }
  
  @media (max-width: 640px) {
    font-size: ${({ theme }) => theme.fontSizes.xl};
  }
`;

/**
 * Styled network summary section
 */
const NetworkSummarySection = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 30px;
  
  @media (max-width: 768px) {
    gap: 15px;
  }
  
  @media (max-width: 640px) {
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

/**
 * Styled summary item
 */
const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  
  @media (max-width: 640px) {
    max-width: 90px;
  }
  
  @media (max-width: 480px) {
    max-width: 70px;
  }
`;

/**
 * Styled summary label
 */
const SummaryLabel = styled.div`
  font-size: 12px;
  opacity: 0.9;
  margin-bottom: 2px;
  color: rgba(255, 255, 255, 0.9);
  
  @media (max-width: 480px) {
    font-size: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/**
 * Styled summary value
 */
const SummaryValue = styled.div`
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  color: white;
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

/**
 * Styled online count
 */
const OnlineCount = styled.span`
  color: #2ecc71;
  text-shadow: 0 0 2px rgba(0, 0, 0, 0.3);
`;

/**
 * Styled total count
 */
const TotalCount = styled.span`
  color: rgba(255, 255, 255, 0.9);
  margin-left: 2px;
`;

/**
 * Styled packet count
 */
const PacketCount = styled.span`
  color: #ecf0f1;
  font-weight: 600;
`;

/**
 * Styled packet count separator
 */
const PacketCountSeparator = styled.span`
  color: rgba(255, 255, 255, 0.9);
  margin: 0 4px;
`;

/**
 * Styled header controls
 */
const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  
  @media (max-width: 640px) {
    gap: 5px;
  }
`;

/**
 * Styled last updated text
 */
const LastUpdatedText = styled.span`
  font-size: 14px;
  opacity: 0.8;
  
  @media (max-width: 640px) {
    display: none;
  }
`;

/**
 * Styled refresh button
 */
const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.3);
  }
  
  @media (max-width: 640px) {
    padding: 5px 8px;
  }
  
  @media (max-width: 480px) {
    padding: 4px 6px;
  }
`;

// Define interface for the MainContent props
interface MainContentProps {
  $sidebarCollapsed: boolean;
}

// Fix layout in MainContent to ensure messages panel fills space
const MainContent = styled.div<MainContentProps>`
  display: flex;
  flex: 1;
  overflow: hidden;
  height: calc(100% - 60px); /* Adjust based on header height */
  position: relative;
  
  /* On mobile devices, make sidebar an overlay */
  @media (max-width: 768px) {
    & > aside {
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      z-index: 9999; /* Increased z-index to ensure it appears above all elements */
      box-shadow: 2px 0 8px rgba(0, 0, 0, 0.3);
      /* Hide initially, but don't use display: none so it can animate in/out */
      transform: ${props => props.$sidebarCollapsed ? 'translateX(-100%)' : 'translateX(0)'};
      transition: transform 0.3s ease;
      overscroll-behavior: contain; /* Prevent scroll chaining */
    }
    
    & > button:first-of-type {
      display: none; /* Still hide the sidebar toggle button */
    }
  }
`;

interface SidebarProps {
  $collapsed: boolean;
}

const SidebarToggle = styled.button<SidebarProps>`
  position: absolute;
  left: ${props => props.$collapsed ? '0' : '300px'};
  top: 50%;
  transform: translateY(-50%);
  z-index: 100;
  width: 24px;
  height: 40px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 0 4px 4px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
  transition: left 0.3s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark || '#2980b9'};
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  /* Hide on mobile */
  @media (max-width: 768px) {
    display: none;
  }
`;

const MapContainer = styled.div`
  position: relative;
  flex: 1;
  height: 100%;
  overflow: hidden;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
`;

// Add a type for the SidebarContent props
interface SidebarContentProps {
  $legendCollapsed: boolean;
}

// Container for the sidebar content
const SidebarContent = styled.div<SidebarContentProps>`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 4px;
  gap: 4px;
  width: 300px; /* Fixed width to match container */
`;

// Updated LatestPacketInfoWrapper for mobile support
const LatestPacketInfoWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1100;
  max-width: 350px;
  
  @media (max-width: 768px) {
    top: 10px;
    left: 10px;
    right: 10px;
    max-width: calc(100% - 20px); /* Account for left and right padding */
    width: calc(100% - 20px);
  }
`;

// Container for the packet info with relative positioning
const PacketInfoContainer = styled.div`
  position: relative;
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 100vw;
    overflow: hidden; /* Prevent children from overflowing */
    position: relative; /* Reinforce relative positioning */
    border-radius: 4px; /* Ensure corners are rounded */
  }
`;

// New collapsed state button
const CollapsedPacketButton = styled.button`
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.9);
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  border: none;
  font-size: 14px;
  font-weight: 600;
  color: #3498db;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  svg {
    margin-left: 6px;
  }
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.95);
  }
`;

// Toggle button for collapse/expand
const CollapseToggle = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 3px;
  color: #3498db;
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  z-index: 1200;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  
  &:hover {
    color: #2980b9;
    background: rgba(255, 255, 255, 0.95);
  }
  
  @media (max-width: 768px) {
    right: 4px; /* Ensure it stays inside the container */
    top: 4px;
    width: 28px; /* Slightly larger touch target on mobile */
    height: 28px;
  }
`;

// Add MenuButton component definition
const MenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textLight};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  cursor: pointer;
  padding: ${({ theme }) => theme.space.xs};
  border-radius: ${({ theme }) => theme.radii.md};
  transition: background-color ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.borderLight};
    color: ${({ theme }) => theme.colors.text};
  }
`;

// Add new InfoButton component
const InfoButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xs};
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.radii.md};
  padding: 2px 6px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  transition: background-color ${({ theme }) => theme.transitions.fast};
  margin-top: -2px;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }
  
  @media (max-width: 640px) {
    padding: 1px 4px;
  }
`;

const InfoButtonText = styled.span`
  @media (max-width: 480px) {
    display: none;
  }
`;

const DesktopOnlyText = styled.span`
  @media (max-width: 480px) {
    display: none;
  }
`;

const MobileOnlyText = styled.span`
  display: none;
  @media (max-width: 480px) {
    display: inline;
  }
`;

// Add a backdrop for mobile sidebar
const SidebarBackdrop = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9000; /* Below sidebar but above everything else */
  }
`;

export default Dashboard;